import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  RefreshCcw, ExternalLink, Copy, Check, ShieldCheck, Loader2,
  Building2, ChevronRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import AdminLayout from '@/components/admin/AdminLayout';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  pending_application: { bg: 'bg-yellow-500/15', text: 'text-yellow-300', border: 'border-yellow-500/30', label: 'Pending review' },
  approved_unpaid:     { bg: 'bg-blue-500/15',   text: 'text-blue-300',   border: 'border-blue-500/30',   label: 'Approved · awaiting payment' },
  active:              { bg: 'bg-[#00FF94]/15',  text: 'text-[#00FF94]',  border: 'border-[#00FF94]/30',  label: 'Active' },
  expired:             { bg: 'bg-red-500/15',    text: 'text-red-400',    border: 'border-red-500/30',    label: 'Expired' },
  cancelled:           { bg: 'bg-white/10',      text: 'text-white/60',   border: 'border-white/20',      label: 'Cancelled' },
};

function AdminPlatformPartners() {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ founding_spots_remaining: 10, founding_spots_total: 10 });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [copied, setCopied] = useState(null);
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/admin/platform-partners`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(res.data?.items || []);
      setMeta({
        founding_spots_remaining: res.data?.founding_spots_remaining ?? 10,
        founding_spots_total: res.data?.founding_spots_total ?? 10,
      });
    } catch {
      toast.error('Failed to load platform partners');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = async (slug) => {
    try {
      setBusy(slug);
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/api/admin/platform-partners/${slug}/approve`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const url = res.data?.checkout_url;
      if (url) {
        await navigator.clipboard.writeText(url).catch(() => {});
        if (res.data?.approval_email_sent) {
          toast.success('Approved · Stripe URL emailed to applicant + copied to clipboard');
        } else {
          toast.success('Approved · Stripe checkout URL copied (email send failed — paste manually)');
        }
      } else {
        toast.warning('Approved, but Stripe checkout link generation failed — try Re-issue');
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Approval failed');
    } finally {
      setBusy(null);
    }
  };

  const reissue = async (slug) => {
    try {
      setBusy(slug);
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/api/admin/platform-partners/${slug}/checkout-link`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.checkout_url) {
        await navigator.clipboard.writeText(res.data.checkout_url).catch(() => {});
        toast.success('Fresh Stripe URL copied');
      } else {
        toast.error('Could not generate fresh checkout link');
      }
    } catch {
      toast.error('Re-issue failed');
    } finally {
      setBusy(null);
    }
  };

  const forceActivate = async (slug) => {
    if (!window.confirm('Mark this partner active immediately? Use this only when they paid via wire / sponsor deal.')) return;
    try {
      setBusy(slug);
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/admin/platform-partners/${slug}/activate`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Partner activated');
      load();
    } catch {
      toast.error('Activation failed');
    } finally {
      setBusy(null);
    }
  };

  const remove = async (slug) => {
    if (!window.confirm(`Permanently delete platform partner "${slug}"? This cannot be undone.`)) return;
    try {
      setBusy(slug);
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/admin/platform-partners/${slug}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Deleted');
      load();
    } catch {
      toast.error('Delete failed');
    } finally {
      setBusy(null);
    }
  };

  const copyText = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      toast.error('Could not copy');
    }
  };

  const filtered = useMemo(() => {
    if (filter === 'all') return items;
    return items.filter((i) => i.status === filter);
  }, [items, filter]);

  const counts = useMemo(() => {
    const c = { all: items.length, pending_application: 0, approved_unpaid: 0, active: 0 };
    items.forEach((i) => { if (c[i.status] !== undefined) c[i.status] += 1; });
    return c;
  }, [items]);

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-platform-partners-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Building2 className="h-6 w-6 text-[#00F0FF]" />
              <h1 className="text-2xl font-bold text-white">Platform Partners</h1>
            </div>
            <p className="text-sm text-white/60 max-w-2xl">
              Paid sponsorship lane for all-in-one SaaS platforms. Founding $99/mo
              (or $899 prepay) · Standard $249/mo. Approve applications, then send
              the Stripe Checkout link from here.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-white/50">
              <span className="text-[#00F0FF] font-bold">{meta.founding_spots_remaining}</span>
              {' '}of {meta.founding_spots_total} Founding spots left
            </span>
            <Button variant="outline" size="sm" onClick={load} disabled={loading}
              data-testid="admin-platforms-refresh-btn"
              className="border-white/20 text-white hover:bg-white/10">
              <RefreshCcw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Filter chips */}
        <div className="flex items-center gap-2 flex-wrap">
          {[
            ['all', `All (${counts.all})`],
            ['pending_application', `Pending (${counts.pending_application})`],
            ['approved_unpaid', `Approved (${counts.approved_unpaid})`],
            ['active', `Active (${counts.active})`],
          ].map(([key, label]) => (
            <button key={key} type="button" onClick={() => setFilter(key)}
              data-testid={`admin-platforms-filter-${key}`}
              className={`text-xs px-3 py-1.5 rounded-full border transition ${
                filter === key
                  ? 'bg-[#00F0FF] text-black border-[#00F0FF] font-semibold'
                  : 'bg-transparent text-white/70 border-white/20 hover:border-white/40'
              }`}>
              {label}
            </button>
          ))}
        </div>

        {/* Items */}
        <div className="space-y-3">
          {loading && filtered.length === 0 ? (
            <div className="rounded-xl border border-white/10 p-8 text-center text-white/50">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="rounded-xl border border-dashed border-white/15 p-8 text-center text-white/60 text-sm">
              No platform partners {filter !== 'all' ? `with status "${filter}"` : 'yet'}.
            </div>
          ) : filtered.map((p) => {
            const sty = STATUS_STYLES[p.status] || STATUS_STYLES.cancelled;
            const stripeUrl = p.stripe_checkout_session_id;
            return (
              <article key={p.partner_id}
                className="rounded-xl border border-white/10 bg-white/[0.03] p-5"
                data-testid={`admin-platform-row-${p.slug}`}>
                <header className="flex items-start justify-between gap-4 flex-wrap mb-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-white font-bold text-base">{p.name}</h3>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${sty.bg} ${sty.text} ${sty.border} border`}>
                        {sty.label}
                      </span>
                      {p.tier === 'founding' && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full font-bold uppercase bg-[#00F0FF]/15 text-[#00F0FF] border border-[#00F0FF]/30">
                          Founding
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-white/50 font-mono">
                      {p.slug} · {p.applicant_email} · applied {new Date(p.applied_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-white font-bold">{p.plan?.replace(/_/g, ' ')}</p>
                    <p className="text-xs text-white/50">${(p.price_cents / 100).toFixed(0)}{p.plan === 'founding_annual' ? ' once' : '/mo'}</p>
                  </div>
                </header>

                {p.pitch && (
                  <p className="text-white/75 text-sm whitespace-pre-wrap leading-relaxed mb-3 border-l-2 border-white/10 pl-3">
                    {p.pitch}
                  </p>
                )}

                {p.current_mrr && (
                  <p className="text-xs text-white/50 mb-3">
                    MRR / stage: <span className="text-white/70 font-semibold">{p.current_mrr}</span>
                  </p>
                )}

                <div className="flex items-center gap-2 flex-wrap mb-3 text-xs">
                  <a href={p.website_url} target="_blank" rel="noopener noreferrer"
                    className="text-white/60 hover:text-white inline-flex items-center gap-1">
                    <ExternalLink className="h-3 w-3" />{p.website_url}
                  </a>
                  {p.applicant_linkedin && (
                    <a href={p.applicant_linkedin} target="_blank" rel="noopener noreferrer"
                      className="text-white/60 hover:text-white inline-flex items-center gap-1">
                      <ExternalLink className="h-3 w-3" />LinkedIn
                    </a>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 flex-wrap pt-3 border-t border-white/5">
                  {p.status === 'pending_application' && (
                    <Button onClick={() => approve(p.slug)} disabled={busy === p.slug} size="sm"
                      data-testid={`admin-platform-approve-${p.slug}`}
                      className="bg-[#00F0FF] text-black hover:bg-[#00F0FF]/90 font-semibold">
                      {busy === p.slug ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5"/> : <ShieldCheck className="h-3.5 w-3.5 mr-1.5" />}
                      Approve &amp; generate Stripe link
                    </Button>
                  )}
                  {p.status === 'approved_unpaid' && (
                    <>
                      <Button onClick={() => reissue(p.slug)} disabled={busy === p.slug} size="sm" variant="outline"
                        data-testid={`admin-platform-reissue-${p.slug}`}
                        className="border-white/20 text-white hover:bg-white/10">
                        Re-issue Stripe link
                      </Button>
                      <Button onClick={() => forceActivate(p.slug)} disabled={busy === p.slug} size="sm" variant="outline"
                        data-testid={`admin-platform-activate-${p.slug}`}
                        className="border-[#00FF94]/40 text-[#00FF94] hover:bg-[#00FF94]/10">
                        Force activate (paid manually)
                      </Button>
                    </>
                  )}
                  {p._admin_stats_url && (
                    <button onClick={() => copyText(p._admin_stats_url, `stats-${p.slug}`)}
                      className="text-xs text-white/60 hover:text-white inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded border border-white/15"
                      data-testid={`admin-platform-stats-${p.slug}`}>
                      {copied === `stats-${p.slug}`
                        ? <><Check className="h-3 w-3 text-[#00FF94]"/>stats URL copied</>
                        : <><Copy className="h-3 w-3"/>copy partner stats URL</>}
                    </button>
                  )}
                  {p.utm_url && (
                    <button onClick={() => copyText(p.utm_url, `utm-${p.slug}`)}
                      className="text-xs text-white/60 hover:text-white inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded border border-white/15"
                      data-testid={`admin-platform-utm-${p.slug}`}>
                      {copied === `utm-${p.slug}`
                        ? <><Check className="h-3 w-3 text-[#00FF94]"/>UTM copied</>
                        : <><Copy className="h-3 w-3"/>copy UTM URL</>}
                    </button>
                  )}
                  <button onClick={() => remove(p.slug)} disabled={busy === p.slug}
                    data-testid={`admin-platform-delete-${p.slug}`}
                    className="text-xs text-red-400 hover:text-red-300 ml-auto">
                    delete
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </AdminLayout>
  );
}

export default AdminPlatformPartners;
